import { useState, useEffect } from 'react';
import { API } from 'aws-amplify';

interface PricingPowerItem {
  asin: string;
  sku: string;
  score: number;
  buybox_pct: number;
  competitors: number;
  current_price: number;
  zoro_cost: number;
  lowest_offer: number;
  margin: number;
}

export default function PricingPowerTable() {
  const [items, setItems] = useState<PricingPowerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<keyof PricingPowerItem>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const result: any = await API.graphql({ query: '{ getPricingPower }' });
        const data = JSON.parse(result.data.getPricingPower);
        setItems(data);
      } catch (e) {
        console.error('Failed to fetch pricing power data:', e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filtered = items.filter(item =>
    !search || item.sku.toLowerCase().includes(search.toLowerCase()) || item.asin.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] ?? 0;
    const bv = b[sortField] ?? 0;
    return sortDir === 'desc' ? (bv as number) - (av as number) : (av as number) - (bv as number);
  }).slice(0, 25);

  const handleSort = (field: keyof PricingPowerItem) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const scoreColor = (score: number) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-600';
  };

  const SortHeader = ({ field, label }: { field: keyof PricingPowerItem; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === 'desc' ? '↓' : '↑')}
    </th>
  );

  if (loading) return <div className="p-6 text-center text-gray-500">Loading pricing power data...</div>;
  if (!items.length) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Pricing Power</h2>
        <input
          type="text"
          placeholder="Search SKU or ASIN..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <p className="text-sm text-gray-500 mb-2">
        Showing top 25{search ? ` matching "${search}"` : ''} by {sortField} ({filtered.length} total)
      </p>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: '600px' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <SortHeader field="score" label="Score" />
                <SortHeader field="sku" label="SKU" />
                <SortHeader field="buybox_pct" label="Buy Box %" />
                <SortHeader field="competitors" label="Comp." />
                <SortHeader field="current_price" label="Price" />
                <SortHeader field="zoro_cost" label="COGS" />
                <SortHeader field="margin" label="Margin" />
                <SortHeader field="lowest_offer" label="Lowest Offer" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sorted.map((item) => (
                <tr key={item.asin} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 text-sm font-bold rounded ${scoreColor(item.score)}`}>
                      {item.score.toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm font-mono">
                    <a href={`https://www.amazon.com/dp/${item.asin}`} target="_blank" rel="noreferrer"
                       className="text-blue-600 hover:underline">{item.sku}</a>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">{(item.buybox_pct * 100).toFixed(1)}%</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">{item.competitors}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">${item.current_price.toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">${item.zoro_cost.toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <span className={item.margin < 0.15 ? 'text-red-600 font-semibold' : ''}>
                      {(item.margin * 100).toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                    {item.lowest_offer > 0 ? `$${item.lowest_offer.toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
